import { Component, signal, inject, OnInit, HostListener, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { Auth } from '@core/services';
import { UserRole } from '@core/interfaces/role.interface';
import { SubReseller as SubResellerService } from '@modules/sub-reseller/services/sub-reseller';
import { DataNotFound } from '@shared/data-not-found/data-not-found';
import { Loader } from '@shared/loader/loader';
import { Pagination } from '@shared/pagination/pagination';
import { Title, Toaster } from '@shared/services';

@Component({
  selector: 'app-sub-reseller',
  imports: [CommonModule, Pagination, ReactiveFormsModule, DataNotFound, Loader],
  templateUrl: './sub-reseller.html',
  styleUrl: './sub-reseller.scss'
})
export class SubReseller implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);
  private subResellerService = inject(SubResellerService);

  isLoading = signal(false);
  isModalOpen = signal(false);
  isDeleteModalOpen = signal(false);
  isEditMode = signal(false);
  showPassword = signal(false);
  isPackageDropdownOpen = signal(false);
  isCompanyDropdownOpen = signal(false);
  isSenderIdDropdownOpen = signal(false);
  subResellers: any = signal([]);
  companyList: any = signal([]);
  activeCompany = signal<string | null>(null);
  subResellerToDelete: any = signal(null);
  totalItems = signal(0);
  pageSize = signal(5);
  currentPage = signal(1);
  searchTerm = signal('');
  packages: any = signal([]);
  resellerDetail: any = signal(null);
  subResellerForm: FormGroup = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    company: ['', [Validators.required]],
    country: ['', [Validators.required]],
    mobile: ['', [Validators.required]],
    smsCreditLimit: [0, [Validators.required, Validators.min(0)]],
    package: ['', [Validators.required]],
    password: ['', [Validators.required, Validators.minLength(6)]],
    senderId: [[], [Validators.required]]
  });

  user = computed(() => this.authService.user || {});
  isAdmin = computed(() => this.user().role?.name === UserRole.ADMIN);

  ngOnInit() {
    this.titleService.setTitle('Sub Reseller');
    this.getCompanyList();
    this.getSubResellers();
  }

  getCompanyList() {
    this.companyList.set([]);
    this.subResellerService.getCompanyList().subscribe({
      next: (response) => {
        this.companyList.set(response.data);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  getPackages(company = '') {
    this.packages.set([]);
    const params = {
      company: company ? company : this.activeCompany()
    };

    this.subResellerService.getPackages(params).subscribe({
      next: (response) => {
        this.packages.set(response.data);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  getResellerDetails(company = '') {
    this.resellerDetail.set(null);
    const params = {
      company: company ? company : this.activeCompany()
    };

    this.subResellerService.getResellerDetails(params).subscribe({
      next: (response) => {
        this.resellerDetail.set(response.data);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
      }
    });
  }

  getSubResellers() {
    this.isLoading.set(true);
    this.subResellers.set([]);

    const queryParams: any = {
      type: UserRole.SUB_RESELLER,
      page: this.currentPage(),
      pageSize: this.pageSize()
    };

    if (this.activeCompany()) {
      queryParams.company = this.activeCompany();
    }

    if (this.searchTerm()) {
      queryParams.search = this.searchTerm();
    }

    this.subResellerService.getSubResellers(queryParams).subscribe({
      next: (response) => {
        this.subResellers.set(response.data);
        this.totalItems.set(response.pagination.total);
        this.isLoading.set(false);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
        this.isLoading.set(false);
      }
    });
  }

  setActiveCompany(company: string) {
    this.activeCompany.set(company);
    this.getSubResellers();
  }

  onSearch(event: any) {
    this.searchTerm.set(event);
    this.currentPage.set(1);
    this.getSubResellers();
  }

  isFilterApplied() {
    return this.searchTerm() !== '';
  }

  clearFilter() {
    this.searchTerm.set('');
    this.currentPage.set(1);
    this.getSubResellers();
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    this.getSubResellers();
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.getSubResellers();
  }

  onAddSubReseller() {
    this.isEditMode.set(false);
    this.subResellerForm.reset({
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      country: '',
      mobile: '',
      smsCreditLimit: 0,
      package: '',
      password: '',
      senderId: []
    });
    this.subResellerForm.get('email')?.enable();
    this.subResellerForm.get('company')?.enable();
    this.subResellerForm
      .get('password')
      ?.setValidators([Validators.required, Validators.minLength(6)]);
    this.subResellerForm.get('password')?.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
    this.isEditMode.set(false);
    this.showPassword.set(false);
    this.isPackageDropdownOpen.set(false);
    this.isCompanyDropdownOpen.set(false);
    this.isSenderIdDropdownOpen.set(false);
  }

  togglePackageDropdown(event: Event) {
    event.stopPropagation();
    this.isCompanyDropdownOpen.set(false);
    this.isPackageDropdownOpen.update((v) => !v);
  }

  toggleCompanyDropdown(event: Event) {
    event.stopPropagation();
    this.isPackageDropdownOpen.set(false);
    this.isCompanyDropdownOpen.update((v) => !v);
  }

  selectCompany(company: string) {
    this.subResellerForm.get('company')?.setValue(company);
    this.isCompanyDropdownOpen.set(false);
    this.getPackages(company);
    this.getResellerDetails(company);
  }

  selectPackage(packageName: string) {
    this.subResellerForm.get('package')?.setValue(packageName);
    this.isPackageDropdownOpen.set(false);
  }

  toggleSenderIdDropdown(event: Event) {
    event.stopPropagation();
    this.isSenderIdDropdownOpen.update((v) => !v);
  }

  isSenderIdSelected(senderId: string): boolean {
    const selected = this.subResellerForm.get('senderId')?.value || [];
    return selected.includes(senderId);
  }

  toggleSenderIdSelection(senderId: string) {
    const currentSelected = this.subResellerForm.get('senderId')?.value || [];
    let newSelected: string[];

    if (currentSelected.includes(senderId)) {
      newSelected = currentSelected.filter((id: string) => id !== senderId);
    } else {
      newSelected = [...currentSelected, senderId];
    }

    this.subResellerForm.get('senderId')?.setValue(newSelected);
  }

  getSelectedSenderIdNames(): string {
    const selected = this.subResellerForm.get('senderId')?.value || [];
    if (selected.length === 0) return 'Select Sender IDs';
    if (selected.length > 2) {
      return `${selected.slice(0, 2).join(', ')} +${selected.length - 2} more`;
    }
    return selected.join(', ');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsidePackage =
      target.closest('.custom-multiselect') || target.closest('.custom-dropdown');
    const isInsideCompany = target.closest('.custom-dropdown');
    const isInsideSenderId = target.closest('.custom-multiselect');

    if (!isInsidePackage && this.isPackageDropdownOpen()) {
      this.isPackageDropdownOpen.set(false);
    }
    if (!isInsideCompany && this.isCompanyDropdownOpen()) {
      this.isCompanyDropdownOpen.set(false);
    }
    if (!isInsideSenderId && this.isSenderIdDropdownOpen()) {
      this.isSenderIdDropdownOpen.set(false);
    }
  }

  getSelectedPackageName(): string {
    return this.subResellerForm.get('package')?.value || 'Select Package';
  }

  togglePasswordVisibility() {
    this.showPassword.update((v) => !v);
  }

  saveReseller() {
    if (this.subResellerForm.invalid) {
      this.toasterService.errorAlert('Please fill in all required fields correctly');
      return;
    }

    const data = this.subResellerForm.getRawValue();
    let userData: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      country: data.country,
      mobile: data.mobile,
      creditLimit: data.smsCreditLimit,
      package: Array.isArray(data.package) ? data.package : [data.package],
      senderId: data.senderId.join(',')
    };

    if (this.isEditMode()) {
      const existingData = this.subResellers().find(
        (subReseller: any) => subReseller.email === data.email
      );
      if (!existingData) {
        this.toasterService.errorAlert('No reseller found for update');
        return;
      }

      this.subResellerService.updateSubReseller(existingData._id, userData).subscribe({
        next: () => {
          this.toasterService.successAlert('Reseller updated successfully!');
          this.getSubResellers();
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error.message);
        }
      });
    } else {
      userData = {
        ...userData,
        email: data.email,
        password: data.password,
        role: UserRole.SUB_RESELLER
      };

      this.subResellerService.addSubReseller(userData).subscribe({
        next: () => {
          this.toasterService.successAlert('Reseller added successfully!');
          this.getSubResellers();
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error.message);
        }
      });
    }

    this.closeModal();
  }

  onEdit(reseller: any) {
    const existingData = this.subResellers().find((user: any) => user.email === reseller.email);

    if (!existingData) {
      this.toasterService.errorAlert('No reseller found for update');
      return;
    }

    this.getPackages(existingData.company);
    this.getResellerDetails(existingData.company);

    this.isEditMode.set(true);
    this.subResellerForm.patchValue({
      firstName: existingData.firstName,
      lastName: existingData.lastName,
      email: existingData.email,
      company: existingData.company,
      country: existingData.country,
      mobile: existingData.mobile,
      smsCreditLimit: existingData.smsCreditLimit,
      package: existingData.packages,
      senderId: existingData.senderId || [],
      password: ''
    });
    this.subResellerForm.get('email')?.disable();
    this.subResellerForm.get('company')?.disable();
    this.subResellerForm.get('password')?.clearValidators();
    this.subResellerForm.get('password')?.updateValueAndValidity();
    this.isModalOpen.set(true);
  }

  onDelete(reseller: any) {
    this.subResellerToDelete.set(reseller);
    this.isDeleteModalOpen.set(true);
  }

  confirmDelete() {
    const reseller = this.subResellerToDelete();
    if (!reseller) {
      this.toasterService.errorAlert('No reseller found for delete');
      return;
    }

    this.subResellerService.deleteSubReseller(reseller._id.toString()).subscribe({
      next: () => {
        this.toasterService.successAlert('Reseller deleted successfully!');
        this.getSubResellers();
        this.closeDeleteModal();
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message || 'Failed to delete reseller');
      }
    });
  }

  closeDeleteModal() {
    this.isDeleteModalOpen.set(false);
    this.subResellerToDelete.set(null);
  }
}
